import { calculateDefaultExpirationDate } from '../utils/configUtilities';
import { getCurrentQuarter } from '../constants/quarterConfig';
import { getVendorCapabilities } from '../config/vendorRegistry';
import { snapToDuration } from '../utils/partNumberUtils';

export const getInitialMetrics = (metricsDb) => ({
  // MORK TODO : Refactor all metrics to be system-level only. Remove all backwards compatible variables after front-end UI refactor.

  // Capacity metrics
  totalVeloSsdCapacity: 0,
  totalHybridSsdCapacity: 0,
  totalAfaSsdCapacity: 0,
  totalSsdCapacity: 0,
  totalHddCapacity: 0,
  totalRawCapacity: 0,
  totalAfaRawCapacity: 0,
  totalCombinedRawCapacity: 0,
  totalCombinedSsdCapacity: 0,
  ratioTotalSsdHdd: 0,
  ratioHybridSsdHdd: 0,

  // Useable capacity metrics
  vpodUseableCapacity: 0,
  afaVpodUseableCapacity: 0,
  veloUseableCapacity: 0,       // MORK TODO : Investigate if used anywhere
  useableEff: 0,
  effectiveEff: 0,
  totalEffectiveCapacity: 0,
  totalAfaEffectiveCapacity: 0,
  totalCombinedEffectiveCapacity: 0,
  totalCompressedEffectiveCapacity: 0,
  totalAfaCompressedEffectiveCapacity: 0,
  totalCombinedCompressedEffectiveCapacity: 0,

  // Performance metrics
  totalIops: 0,
  totalAfaIops: 0,
  totalInodes: 0,
  totalMetadata: 0,
  totalReadTransferRate: 0,
  totalWriteTransferRate: 0,
  totalAfaReadTransferRate: 0,
  totalAfaWriteTransferRate: 0,
  totalBurstReadTransferRate: 0,
  totalBurstWriteTransferRate: 0,
  // totalAfaBurstReadTransferRate: 0,    // MORK TODO : AFA Burst Rate doesn't make sense - burst is for hybrid nvme only....
  // totalAfaBurstWriteTransferRate: 0,   // MORK TODO : AFA Burst Rate doesn't make sense - burst is for hybrid nvme only....

  // Cost metrics
  totalSolutionCost: 0,
  totalAfaSetCost: 0,
  totalCombinedSolutionCost: 0,
  totalServiceCost: 0,
  ssPercenatge: 0,
  swPercentage: 0,
  totalSoftwareCost: 0,
  hddSoftwareCost: 0,
  ssdSoftwareCost: 0,
  totalSoftwarecost: 0,
  afaSsdSoftwareCost: 0,
  dollarsPerRawTB: 0,
  dollarsPerUseableTB: 0,
  dollarsPerEffectiveTB: 0,
  dollarsPerCompressedEffectiveTB: 0,
  vduraMarginDollars: 0,
  vduraMarginPoints: 0,

  // Physical metrics
  totalNominalPower: 0,
  totalAfaNominalPower: 0,
  totalCombinedNominalPower: 0,
  totalWeight: 0,
  totalAfaWeight: 0,
  totalCombinedWeight: 0,
  totalRU: 0,
  totalAfaRU: 0,
  totalCombinedRU: 0
});

// Move all your initial config here
export const getInitialConfig = (productDb, hardwareVendor = 'AIC') => {
  // MORK TODO : We could add a structure to the config that standardizes what type of configuration is being used (Base, Expansion, TDS, etc)
  //             This could also be used in conjuction with a Part Number structure that allows us to load/select the partnumbers based on the config

  // Vendor-dependent defaults. The first render can now be on Dell or
  // Supermicro (ConfiguratorGate picks the user's first accessible vendor), and
  // useConfigurationLogic captures this object once at mount. Rule R9 indexes
  // productMappings.hybridHdd[jbodSize] unguarded, so an AIC 78 against a
  // 60/120 or 60/90 map throws — the vendor-switch path only ever survived
  // because getVendorConfigOverrides set defaultJbodSize in the same batch.
  // For AIC these resolve to the historical literals (78, 'standard', 36).
  const caps = getVendorCapabilities(hardwareVendor);

  return {
    // Customer configuration options
    quarter: getCurrentQuarter(),
    subscriptionMonths: caps.subscriptionDurations?.length
      ? snapToDuration(36, caps.subscriptionDurations)
      : 36,
    serviceOption: caps.serviceOptions[0].key,

    // System Type Options - MORK TODO : What are the config business rules that need to be applied?
    systemType: "BaseSystem",       // "BaseSystem", "Expansion"

    // Network Options
    // MORK TODO : add connector variable for interface to separate between ssd and hdd
    // CONF-93: option ids come from vendorRegistry.capabilities.networkOptions.
    // 200GbE is the default (user decision 2026-09-17): it is what legacy
    // 'Ethernet' resolves to, same Broadcom card and same CCI slot 1 bundle as
    // 100GbE. It must NOT be 100GbE — that card's 40/40 ceiling clamps both
    // protocols, so an RDMA default would report identical throughput to TCP
    // and the protocol choice would look broken out of the box.
    interfaceOption: "200GbE",
    // CONF-93: RDMA is the default transport (user decision 2026-09-16). Safe
    // as a default because compressionRatio starts at 1.0 — RDMA uncompressed
    // is a measured 74/50. RDMA *compressed* has no measured rate and reports
    // 0 GB/s, which the configurator warns about at the point of selection.
    // Saved quotes are deliberately NOT migrated to RDMA; see migration.jsx.
    networkProtocol: "RDMA",
    compressionRatio: 1.0,         // Legacy compression ratio for system level - MORK TODO will remove eventually
    endToEndEncryption: false,      // Legacy E2E for system level - will remove eventually

    // Vendor Options
    hardwareVendor: hardwareVendor,

    // Rack Size Options
    rackSize: 42,                    // 42U by default, can be 25U, 48U, No Rack, etc

    // Registration status
    isNoReg: true,
    // Consultation Mode - hides BOM and pricing
    consultationMode: false,

    // Metric Unit Options
    // Default is Decimal
    isPBUnit: true,           // true = PB, false = TB

    // CCI is the default quote path — the Quote Details toggle is an opt-out.
    // Configs saved before this flag existed have no key and inherit `true`
    // through the load merge, which is intended. See the 2026-08-03 CCI spec.
    cciEnabled: true,

    // Director configuration
    veloCount: 3,
    veloSsdCapacity: 1.92,

    // Hybrid storage configuration
    hybridEnabled: true,
    hybridMinRawCapacity: 3000,
    hybridNodeCount: 3,
    jbodSize: caps.defaultJbodSize,
    hybridHddCapacity: 24,
    hybridVpodSsdCapacity: 3.84,
    hybridEncodingScheme: "4+2",
    hybridSpares: 2,
    hybridMLEC: false,
    hybridCompressionRatio: 1.25,
    hybridEndToEndEncryption: false,

    // Track if user has made explicit choices MORK TODO : this is a temporary solution to a larger problem of statemanagement
    // How should we track intentional user changes vs automatic changes?
    hybridEncodingSchemeUserSelected: false,
    afaEncodingSchemeUserSelected: false,

    // AFA storage configuration
    afaEnabled: false,
    afaMinRawCapacity: 0,
    afaNodeCount: 0,
    afaVpodSsdCapacity: 7.68,
    afaEncodingScheme: "4+2",
    afaSpares: 2,
    afaCompressionRatio: 1.0,
    afaEndToEndEncryption: false,

    // Pricing
    // Default is $225 for ssd and $19 for hdd, matching the 2026-Q3 VDURACare
    // book price. These are only a seed: the quarter/service rule in
    // domain/rules/pricingRules.ts reloads them from productDb — but ONLY when
    // the quarter or service option changes, so it never fires at mount. That
    // is why these literals have to be right: until someone touches the quarter
    // dropdown, they ARE the quote's software price. HDD sat at 16 against a
    // book of 19 and quietly undercharged every fresh hybrid quote.
    // MORK TODO : Base Software Pricing should be integrated into productDb or "eng" file that gets loaded from the server or somewhere else
    // License Unit Price = base Software Unit Price - Support Unit Price
    discountMonths: 0,
    ssdSoftware: 225,
    hddSoftware: 19,
    baseSsdSoftware: 225,
    baseHddSoftware: 19,
    ssdLicenseUnitPrice: 222,
    ssdSupportUnitPrice: 3,
    hddLicenseUnitPrice: 18.7,
    hddSupportUnitPrice: 0.3,

    // Quote details
    preparedFor: '',
    partnerName: '',              // Optional
    quoteDescription: '',
    notes: 'V5000 Configuration',
    opportunityID: '',
    customerID: '',               // MORK TODO: Legacy value
    customerNumber: '',           // MORK TODO: Legacy value
    offerNumber: '',              // Needs to rename to Quote Number - is combo of salesforce quote number + configurator quote serial
    configuratorQuoteSerial: '',        // AWS Generated Serial Number for new quote
    salesforceQuoteNumber: '',          // Default number for new quotes
    accountExecutive: '',
    accountExecutiveEmail: '',
    warrantyStartDate: '',                                // Optional
    quoteExpirationDate: calculateDefaultExpirationDate(),    // Defaults to 30 days after today, SpecialPricing can change it

    // Available options (constants)
    availableSpares: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26],

    // Sizing mode: false = capacity-driven (default), true = node-count-driven
    sizeByNodeCount: false
  };
};

// Some metadata state variables for the config to track loading and pricing state
export const getInitialMetaState = () => ({
  isPricingLocked: false,
  isLoadingConfig: false,
  lastLoadedAt: null,
  // Future: Add more meta flags here as needed
  // isCalculating: false,  // Could move this here
  // hasUnsavedChanges: false,
});
